/**
 * Which OS-shell deck a URL belongs to — the ONE pure classifier, shared by
 * App.tsx's live `deckKind` and the hydration guard that seeds each deck's
 * frozen location (#504).
 *
 * ── Matched on the FIRST segment, not by substring ──────────────────
 *
 * These used to be substring `includes` tests over the whole path, which read
 * an occurrence anywhere. `/billing/platform/settings` — the platform console's
 * own settings destination — contains the settings segment, and testing that
 * branch before billing opened the SETTINGS deck instead of the console. The
 * deck an app owns is decided by the segment right after the tenant prefix, so
 * that is what is compared; `launcher`/`chat`/`drive` are anchored regexes for
 * the same reason (a substring test would claim any URL merely containing the
 * word). `apps/web/src/__tests__/deck-kind-segment.test.ts` guards against the
 * old form returning here.
 *
 * `null` = a non-deck route (a user app `/~/app/:id`, a bare `/file/:id`, or a
 * segment that USED to be a deck route and was renamed away — e.g. `/~/miniapps`
 * after the pdapps rename shipped with no redirect). That last case is why the
 * hydration guard exists: a durable persisted drive route can outlive the route
 * it named.
 */
import type { ShellAppKind } from '@papan/core-ui/ui'

/**
 * A deck kind — the system-app kinds a URL can classify into. Excludes the
 * user-app kinds (`miniapp`/`html`), which are never route-classified: they own
 * `/~/app/:id` and carry a ref, not a deck segment. Narrower than `ShellAppKind`
 * so `deckKind` stays assignable to the taskbar's `View`.
 */
export type DeckKind = Exclude<ShellAppKind, 'miniapp' | 'html'>

/**
 * A URL inside the workspace deck. Exported as the ONE copy: App.tsx derives
 * `isWorkspaceView` (and downstream workspace behaviour) from the same regex,
 * so a divergent second copy would let `classifyDeck` and `isWorkspaceView`
 * disagree about whether a path is a workspace — the classifier-vs-consumer
 * split this module exists to avoid.
 */
export const WORKSPACE_PATH_RE = /(?:\/~|\/t\/[^/]+)\/workspace(?:\/|$)/
const LAUNCHER_PATH_RE = /(?:^|\/t\/[^/]+|\/~)\/launcher(?:\/|$)/
const CHAT_PATH_RE = /(?:^|\/t\/[^/]+|\/~)\/chat(?:\/|$)/
// Keep in sync with the drive routes mounted in App.tsx. A segment renamed out
// of here (with no redirect) turns every persisted drive route naming it stale.
const DRIVE_PATH_RE = /(?:^|\/t\/[^/]+|\/~)\/(?:folder|home|recent|projects|pdapps|shared|search|bin|quarantine)(?:\/|$)/

/** The deck that owns `pathname`, or null for a non-deck route. */
export function classifyDeck(pathname: string): DeckKind | null {
  const segment = pathname.replace(/^\/(?:t\/[^/]+|~)/, '').split('/')[1] ?? ''
  if (WORKSPACE_PATH_RE.test(pathname)) return 'workspace'
  if (segment === 'keybook') return 'keybook'
  if (segment === 'settings') return 'settings'
  if (segment === 'billing') return 'billing'
  // The taskbar's PDApps window (`/~/apps`). Not `/~/pdapps` — that is the
  // DRIVE's PDApps page, a drive route below.
  if (segment === 'apps') return 'apps'
  if (LAUNCHER_PATH_RE.test(pathname)) return 'launcher'
  if (CHAT_PATH_RE.test(pathname)) return 'chat'
  if (DRIVE_PATH_RE.test(pathname)) return 'drive'
  return null
}

/**
 * Parse a server-persisted deck route into a frozen `{pathname, search}` — but
 * ONLY when it still classifies as `kind`. A stored route whose deck has been
 * renamed/removed (or that never matched its slot) is rejected as `null`, so the
 * caller falls back to the deck's default entry instead of navigating to a dead
 * path that the `*` catch-all bounces to the landing surface (#504).
 */
export function restoredDeckRoute(
  kind: DeckKind,
  route: string,
): { pathname: string; search: string } | null {
  const q = route.indexOf('?')
  const pathname = q === -1 ? route : route.slice(0, q)
  const search = q === -1 ? '' : route.slice(q)
  return classifyDeck(pathname) === kind ? { pathname, search } : null
}
